import { EMPTY, of } from 'rxjs';
import { mergeMap, withLatestFrom, map, catchError, takeUntil, mapTo, switchMap, startWith, filter } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import CustomData from '../customData.json';
import * as BookingService from '../service/BookingService';
import * as Booking from '../constants/Booking';
import * as BookingActions from '../actions/BookingActions';
import * as CommonActions from '../actions/CommonActions';
import { isNotNull, removeCountryCode } from '../global/function';
import * as USER from '../constants/User';
import { BookingCategory, BookingRecord, BookingItem, bookingSelecter, memberIdSelector } from '../utils/BookingUtils';
import i18n from '../i18n';
import { findNearestAvailableDate } from '../utils/CalendarUtils';
import { getTimeslotParams, isTimeslotDataPending } from '../utils/TimeslotUtils';

const getBookingListEpic = (action$, state$) => action$.pipe(
    ofType(Booking.GET_BOOKING_LIST),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
        const { locale } = state.common;
        const { sid: shopId } = state.common;

        if (!shopId) return of(BookingActions.getBookingListRejectedAction());

        const params = {
            shopId,
            lang: locale,
        }

        return BookingService.getMbookingProductList(params).pipe(
            map((res) => {
                if (res.success) {
                    let categories = []
                    if (Array.isArray(res.productType)) {
                        categories = res.productType.map(e => BookingCategory(e));
                    }

                    let itemsByCategory = {};
                    if (Array.isArray(res.product)) {
                        categories.forEach(category => {
                            itemsByCategory[category.id] = res.product.filter(e => e.deleted != 1 && (e.typeId === category.id || category.id == 0)).map(e => BookingItem(e, res.branch))
                        });
                    }

                    return BookingActions.getBookingListFulfilledAction(categories, itemsByCategory, res.bannerImg, res.memberOnly, res.shopIcon, res.description, res.grade_available, res.customizedFields);
                }
                return BookingActions.getBookingListRejectedAction();
            }),
            catchError((error) => of(BookingActions.getBookingListRejectedAction(`${error}`))),
            takeUntil(action$.pipe(ofType(Booking.GET_BOOKING_LIST_CANCELLED))),
        );
    })
);

const submitBookingEpic = (action$, state$) => action$.pipe(
    ofType(Booking.SUBMIT_BOOKING),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
        const { locale } = state.common;
        const { form } = state.booking;
        const { sid: shopId } = state.common;

        const defaultValues = {
            [Booking.FieldNames.location]: 0,
            [Booking.FieldNames.numberOfPeople]: 0,
            [Booking.FieldNames.timeslot]: '',
            [Booking.FieldNames.remark]: '',
            [Booking.FieldNames.memberId]: '',
        }

        const params = {
            shopId,
            lang: locale,
            ...defaultValues,
            ...form,
        };

        return BookingService.submitMbooking(params).pipe(
            map(res => {
                if (res.success) {
                    return BookingActions.submitBookingFulfilledAction(BookingRecord(res.productRecord, res.branch, res.product));
                }
                return BookingActions.submitBookingRejectedAction(null, res.message);
            }),
            catchError(error => of(BookingActions.submitBookingRejectedAction(error))),
            takeUntil(action$.pipe(ofType(Booking.SUBMIT_BOOKING_CANCELLED))),
        );
    })
);

const getRecordListEpic = (action$, state$) => action$.pipe(
    ofType(Booking.GET_RECORD_LIST),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
        const { locale: lang } = state.common;
        const { sid: shopId } = state.common;

        if (!shopId) return of(BookingActions.getRecordListRejectedAction());

        let phone = state.common.phoneNumberList[shopId];
        let memberId = state.common.memberIdList[shopId];

        phone = memberId ? '' : phone

        const params = {
            shopId,
            memberId,
            phone,
            lang,
        }

        return BookingService.getMbookingRecordList(params).pipe(
            map(res => {
                if (res.success) {
                    let all = [];
                    let pending = [];
                    let success = [];
                    let fail = [];
                    let checkedIn = [];

                    if (res.productRecord && Object.keys(res.productRecord).length > 0) {
                        pending = res.productRecord.pending.map(e => BookingRecord(e, res.branch, res.product));
                        success = res.productRecord.success.map(e => BookingRecord(e, res.branch, res.product));
                        fail = res.productRecord.fail.map(e => BookingRecord(e, res.branch, res.product));
                        checkedIn = res.productRecord.checked_in.map(e => BookingRecord(e, res.branch, res.product));

                        // the api may serve the combined list itself; fall back to merging the buckets
                        all = Array.isArray(res.productRecord.all)
                            ? res.productRecord.all.map(e => BookingRecord(e, res.branch, res.product))
                            : [...pending, ...success, ...checkedIn, ...fail];
                    }

                    return BookingActions.getRecordListFulfilledAction(all, pending, success, fail, checkedIn, phone);
                }
                return BookingActions.getRecordListRejectedAction();
            }),
            catchError(error => of(BookingActions.getRecordListRejectedAction(`${error}`))),
            takeUntil(action$.pipe(ofType(Booking.GET_RECORD_LIST_CANCELLED))),
        )
    })
)

const selectBookingItemEpic = (action$, state$) => action$.pipe(
    ofType(Booking.SELECT_BOOKING_ITEM),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
        const { bookingItem } = action.payload;
        const memberId = memberIdSelector(state);
        const { id: productId, branches, date } = bookingItem;
        const { phone, name } = state.user.userInfo

        let form = {};

        // auto select if there are only one branch
        if (isNotNull(branches) && Array.isArray(branches) && branches.length === 1) {
            form[Booking.FieldNames.location] = branches[0].id;
        }

        // nearest date the calendar lets the user pick, left empty if there is none
        const nearestDate = findNearestAvailableDate(date);
        if (isNotNull(nearestDate)) {
            form[Booking.FieldNames.date] = nearestDate;
        }

        if (isNotNull(productId)) {
            form[Booking.FieldNames.productId] = productId;
        }

        if (isNotNull(memberId)) {
            form[Booking.FieldNames.memberId] = memberId;
        }

        if (isNotNull(phone)) {
            form[Booking.FieldNames.phone] = removeCountryCode(phone);
        }

        if (isNotNull(name)) {
            form[Booking.FieldNames.name] = name;
        }

        // pre-select a timeslot only when the user never had to pick a date or a branch
        const isBranchSettled = !Array.isArray(branches) || isNotNull(form[Booking.FieldNames.location]);

        if (isNotNull(nearestDate) && isBranchSettled) {
            return of(
                BookingActions.setTimeslotPreselectAction({
                    date: form[Booking.FieldNames.date],
                    branch: form[Booking.FieldNames.location],
                }),
                BookingActions.updateBookingFormAction(form),
            );
        }

        return of(BookingActions.updateBookingFormAction(form));
    })
);

const updateTimeslotsEpic = (action$, state$) => action$.pipe(
    ofType(Booking.UPDATE_BOOKING_FORM),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
        const { locale: lang } = state.common;
        const { selectedBookingItem, prevForm, form, availableTimeslots, timeslotParams } = state.booking;
        const { sid: shopId } = state.common;

        const { supportTimeslotSelect = false, id: productId, source = {} } = selectedBookingItem;

        // exactly one of the two timeslot apis runs
        if (source.timeslotGroupOn) return EMPTY;

        const prevBranchId = prevForm[Booking.FieldNames.location];
        const prevDate = prevForm[Booking.FieldNames.date];

        const branchId = form[Booking.FieldNames.location];
        const date = form[Booking.FieldNames.date];

        if (
            date &&
            !(prevBranchId === branchId && prevDate === date)
        ) {
            // responses carry the date / branch they belong to, so that a late one is not taken for the current
            const requestedParams = getTimeslotParams(form);

            const params = {
                shopId,
                productId,
                ...requestedParams,
                lang,
            }

            return BookingService.getMbookingProductAvaliableTimeslot(params).pipe(
                map((res) => {
                    if (res.success) {
                        const { timeslot = [] } = res;
                        const filteredTimeslots = timeslot.filter(i => i.enable);
                        if (supportTimeslotSelect) {
                            return BookingActions.getAvailableTimeslotsFulfilledAction(filteredTimeslots, requestedParams);
                        } else {
                            if (filteredTimeslots.length === 0) {
                                alert(i18n.t(['BOOKING', 'NOT_AVAILABLE_DATE']));
                                return BookingActions.notAvailableDateAction();
                            } else {
                                return BookingActions.getAvailableTimeslotsFulfilledAction(availableTimeslots, requestedParams);
                            }
                        }
                    }
                    return BookingActions.getAvailableTimeslotsRejectedAction(null, requestedParams);
                }),
                catchError(error => of(BookingActions.getAvailableTimeslotsRejectedAction(error, requestedParams))),
                takeUntil(action$.pipe(ofType(Booking.GET_AVAILABLE_TIMESLOTS_CANCELLED))),
            );
        }

        return of(BookingActions.getAvailableTimeslotsFulfilledAction(availableTimeslots, timeslotParams));
    })
);

const timeslotGroupKeys = [
    Booking.FieldNames.location,
    Booking.FieldNames.date,
    Booking.FieldNames.memberId,
    Booking.FieldNames.phone,
];

const updateTimeslotGroupEpic = (action$, state$) => action$.pipe(
    ofType(Booking.UPDATE_BOOKING_FORM),
    withLatestFrom(state$),
    // only the params of the grouped api matter, so that e.g. picking the number of
    // people does not refetch (and does not cancel a request already in flight)
    filter(([action, state]) => {
        const { selectedBookingItem, prevForm, form } = state.booking;
        const { source = {} } = selectedBookingItem;

        if (!source.timeslotGroupOn) return false;

        // branch and date are optional, so the groups are fetched once the form opens;
        // prevForm is only empty for the update that fills in the initial values
        if (Object.keys(prevForm).length === 0) return true;

        return timeslotGroupKeys.some(key => prevForm[key] !== form[key]);
    }),
    switchMap(([action, state]) => {
        const { locale: lang } = state.common;
        const { sid: shopId } = state.common;
        const { selectedBookingItem, form } = state.booking;

        const { id: productId } = selectedBookingItem;

        const branchId = form[Booking.FieldNames.location];

        const params = {
            shopId,
            lang,
            productId,
            branchId: branchId ? branchId : null,
            date: form[Booking.FieldNames.date],
            memberId: form[Booking.FieldNames.memberId],
            phone: form[Booking.FieldNames.phone],
        };

        return BookingService.getMbookingProductTimeslotGroup(params).pipe(
            map(res => {
                if (res.success) {
                    return BookingActions.getTimeslotGroupFulfilledAction(res);
                }
                return BookingActions.getTimeslotGroupRejectedAction();
            }),
            catchError(error => of(BookingActions.getTimeslotGroupRejectedAction(`${error}`))),
            takeUntil(action$.pipe(ofType(Booking.GET_TIMESLOT_GROUP_CANCELLED))),
            startWith(BookingActions.getTimeslotGroupAction()),
        );
    })
);

const updateAvailablePeopleEpic = (action$, state$) => action$.pipe(
    ofType(Booking.SELECT_BOOKING_ITEM, Booking.GET_AVAILABLE_TIMESLOTS_FULFILLED, Booking.GET_TIMESLOT_GROUP_FULFILLED, Booking.UPDATE_BOOKING_FORM),
    withLatestFrom(state$),
    map(([action, state]) => {
        const { selectedBookingItem, form, availableTimeslots } = state.booking;
        // BookingItem() sets numberOfPeople to null when the product has no people field
        const numberOfPeople = selectedBookingItem.numberOfPeople ?? [];
        const selectedTimeslot = form[Booking.FieldNames.timeslot];

        if (selectedTimeslot) {
            const timeslotData = availableTimeslots.find(t => t.value === selectedTimeslot);
            if (timeslotData && timeslotData.peopleLimit) {
                return BookingActions.updateAvailablePeople(numberOfPeople.filter(e => e <= timeslotData.peopleLimit));
            }
        }

        return BookingActions.updateAvailablePeople(numberOfPeople);
    })
);

const fixBookingFormEpic = (action$, state$) => action$.pipe(
    ofType(Booking.GET_AVAILABLE_TIMESLOTS_FULFILLED, Booking.GET_TIMESLOT_GROUP_FULFILLED, Booking.UPDATE_BOOKING_FORM),
    withLatestFrom(state$),
    map(([action, state]) => {
        const { form, availableTimeslots, availableNumberOfPeople } = state.booking;
        const updatedForm = { ...form }

        let formFixed = false;

        const timeslotValues = availableTimeslots.map(e => e.value);
        const selectedTimeslot = form[Booking.FieldNames.timeslot];
        // keep the selection while the timeslots are being refetched, the shown ones are still the previous
        if (selectedTimeslot && !isTimeslotDataPending(state.booking) && !timeslotValues.includes(selectedTimeslot)) {
            delete updatedForm[Booking.FieldNames.timeslot];
            formFixed = true;
        }

        const selectedNumberOfPeople = form[Booking.FieldNames.numberOfPeople];
        if (selectedNumberOfPeople && !availableNumberOfPeople.includes(selectedNumberOfPeople)) {
            delete updatedForm[Booking.FieldNames.numberOfPeople];
            formFixed = true;
        }

        if (formFixed) {
            return BookingActions.fixBookingFormAction(updatedForm);
        }

        return BookingActions.fixBookingFormAction();
    })
)

// select the first bookable timeslot once, as soon as the slots of the pre-selected date are loaded
const preselectTimeslotEpic = (action$, state$) => action$.pipe(
    ofType(
        Booking.GET_AVAILABLE_TIMESLOTS_FULFILLED,
        Booking.GET_AVAILABLE_TIMESLOTS_REJECTED,
        Booking.GET_TIMESLOT_GROUP_FULFILLED,
        Booking.GET_TIMESLOT_GROUP_REJECTED,
    ),
    withLatestFrom(state$),
    // the user has already changed the form, or the slots of the current date are not in yet
    filter(([action, state]) => !!state.booking.timeslotPreselect && !isTimeslotDataPending(state.booking)),
    mergeMap(([action, state]) => {
        const { form, availableTimeslots } = state.booking;

        // the api only enables bookable slots and sends them in time order; in grouped mode
        // availableTimeslots already holds the enabled slots of the enabled groups only
        const timeslot = availableTimeslots[0];

        if (!timeslot) {
            return of(BookingActions.setTimeslotPreselectAction(null));
        }

        return of(
            BookingActions.setTimeslotPreselectAction(null),
            BookingActions.updateBookingFormAction({ ...form, [Booking.FieldNames.timeslot]: timeslot.value }),
        );
    })
);

const savePhoneEpic = (action$, state$) => action$.pipe(
    ofType(Booking.SUBMIT_BOOKING_FULFILLED),
    withLatestFrom(state$),
    map(([action, state]) => {
        const sid = state.common.sid;
        const { phone } = state.booking.record ?? {};
        return CommonActions.savePhone(sid, phone);
    })
)

const updateBookingEpic = action$ => action$.pipe(
    ofType(Booking.SUBMIT_BOOKING_FULFILLED, USER.LOGOUT),
    mapTo(BookingActions.getBookingListAction()),
)

const updateRecordListEpic = action$ => action$.pipe(
    ofType(Booking.SUBMIT_BOOKING_FULFILLED),
    mapTo(BookingActions.getRecordListAction())
)

const resetBookingEpic = action$ => action$.pipe(
    ofType(USER.LOGOUT),
    mapTo(BookingActions.resetBookingAction()),
)

export default [
    getBookingListEpic,
    submitBookingEpic,
    getRecordListEpic,
    selectBookingItemEpic,
    savePhoneEpic,
    updateBookingEpic,
    updateRecordListEpic,
    resetBookingEpic,
    updateTimeslotsEpic,
    updateTimeslotGroupEpic,
    updateAvailablePeopleEpic,
    fixBookingFormEpic,
    preselectTimeslotEpic,
];